import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api, { downloadTemplate } from "../api";
import { Modal, useTags, TagChips, TagPicker, KindBadge, KINDS } from "../components/ui";
import QuickActions from "../components/QuickActions";
import usePersistedState from "../hooks/usePersistedState";

const EMPTY = {
  name: "", kind: "cliente", vat_number: "", fiscal_code: "", email: "", pec: "",
  phone: "", sdi_code: "", address: "", city: "", province: "", zip_code: "", notes: "", tag_ids: [],
  acquisition_year: "", margin_per_purchase: "", purchase_frequency: "", referred_by: "",
};

const emailOk = (e) => !e || /^[^@\s;,]+@[^@\s;,]+\.[A-Za-z]{2,}$/.test(String(e).trim());

export default function Companies({ embedded }) {
  const { tags } = useTags();
  const [list, setList] = useState([]);
  const [q, setQ] = usePersistedState("companies.q", "");
  const [kind, setKind] = usePersistedState("companies.kind", "");
  const [tagFilter, setTagFilter] = usePersistedState("companies.tag", "");
  const [serviceFilter, setServiceFilter] = usePersistedState("companies.service", "");
  const [services, setServices] = useState([]);
  const [sortBy, setSortBy] = usePersistedState("companies.sortBy", "name");
  const [order, setOrder] = usePersistedState("companies.order", "asc");
  const [allCompanies, setAllCompanies] = useState([]);
  const [modal, setModal] = useState(null); // {data, editing}

  const load = useCallback(() => {
    const params = new URLSearchParams();
    // la ricerca guarda TUTTI i contatti: ignora i filtri tipo/etichetta/servizio
    if (q) params.set("q", q);
    if (!q && kind) params.set("kind", kind);
    if (!q && tagFilter) params.set("tag_id", tagFilter);
    if (!q && serviceFilter) params.set("service", serviceFilter);
    params.set("sort_by", sortBy);
    params.set("order", order);
    api.get(`/companies?${params.toString()}`).then((r) => setList(r.data)).catch(() => {});
  }, [q, kind, tagFilter, serviceFilter, sortBy, order]);

  useEffect(() => { api.get("/companies/services").then((r) => setServices(r.data)).catch(() => {}); }, []);

  useEffect(() => { api.get("/companies").then((r) => setAllCompanies(r.data)).catch(() => {}); }, [modal]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    const { data, editing } = modal;
    const payload = { ...data };
    Object.keys(payload).forEach((k) => { if (payload[k] === "") delete payload[k]; });
    payload.tag_ids = data.tag_ids;
    try {
      if (editing) await api.put(`/companies/${data.id}`, payload);
      else await api.post("/companies", payload);
      setModal(null); load();
    } catch (e) {
      alert("Errore: " + (e.response?.data?.detail || e.message));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Eliminare l'azienda e i suoi progetti/documenti?")) return;
    await api.delete(`/companies/${id}`); load();
  };

  const f = (k, v) => setModal((m) => ({ ...m, data: { ...m.data, [k]: v } }));

  return (
    <div>
      {!embedded && <h1 className="page-title">Aziende</h1>}
      {!embedded && <p className="page-sub">Anagrafica di livello 1 — clienti e lead</p>}

      <div className="toolbar">
        <input style={{ maxWidth: 260 }} placeholder="Cerca per nome, email, P.IVA…" value={q} onChange={(e) => setQ(e.target.value)} />
        <select style={{ maxWidth: 160, opacity: q ? 0.5 : 1 }} value={kind} onChange={(e) => setKind(e.target.value)} disabled={!!q} title={q ? "Durante la ricerca vengono mostrati tutti i contatti" : ""}>
          <option value="">Tutti i tipi</option>
          {KINDS.map((k) => <option key={k.value} value={k.value}>{k.label}</option>)}
        </select>
        <select style={{ maxWidth: 180, opacity: q ? 0.5 : 1 }} value={tagFilter} onChange={(e) => setTagFilter(e.target.value)} disabled={!!q}>
          <option value="">Tutte le etichette</option>
          {tags.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
        <select style={{ maxWidth: 220, opacity: q ? 0.5 : 1 }} value={serviceFilter} onChange={(e) => setServiceFilter(e.target.value)} disabled={!!q} data-testid="service-filter">
          <option value="">Tutti i servizi venduti</option>
          {services.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        {(q || kind || tagFilter || serviceFilter) && (
          <button className="btn sm" onClick={() => { setQ(""); setKind(""); setTagFilter(""); setServiceFilter(""); }} data-testid="companies-clear-filters">Azzera filtri</button>
        )}
        <div className="spacer" />
        <Link className="btn" to="/import">⬆ Importa da Excel</Link>
        <button className="btn" onClick={() => downloadTemplate("companies").catch((e) => alert("Errore: " + (e.response?.data?.detail || e.message)))}>⬇ Template Excel</button>
        <button className="btn primary" onClick={() => setModal({ data: { ...EMPTY }, editing: false })}>+ Nuova azienda</button>
      </div>

      <div className="toolbar">
        <span className="muted">Ordina per:</span>
        <select style={{ maxWidth: 200 }} value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
          <option value="name">Nome</option>
          <option value="margin">Margine annuo</option>
          <option value="frequency">Frequenza di acquisto</option>
          <option value="referrals">Porta altri (referral)</option>
          <option value="acquisition_year">Anno acquisizione</option>
        </select>
        <select style={{ maxWidth: 140 }} value={order} onChange={(e) => setOrder(e.target.value)}>
          <option value="asc">Crescente</option>
          <option value="desc">Decrescente</option>
        </select>
      </div>

      <table>
        <thead><tr><th>Ragione sociale</th><th>Tipo</th><th>Margine annuo</th><th>Freq.</th><th>Porta altri</th><th>Anno</th><th>Città</th><th></th></tr></thead>
        <tbody>
          {list.map((c) => (
            <tr key={c.id}>
              <td>
                <Link to={`/aziende/${c.id}`}>{c.name}</Link>
                {!emailOk(c.email) && (
                  <span style={{ marginLeft: 6, fontSize: 11, color: "#fb7185", fontWeight: 700 }} title={`Email non valida: "${c.email}" — aprila con Modifica e correggila`} data-testid={`bad-email-${c.id}`}>
                    ⚠ email non valida
                  </span>
                )}
              </td>
              <td><KindBadge kind={c.kind} /></td>
              <td>{c.annual_margin ? `€ ${c.annual_margin}${c.reference_year ? ` · ${c.reference_year}` : ""}` : "—"}</td>
              <td>{c.purchase_frequency || "—"}</td>
              <td>{c.referral_count || 0}</td>
              <td>{c.acquisition_year || "—"}</td>
              <td>{c.city || "—"}</td>
              <td style={{ whiteSpace: "nowrap" }}>
                <button className="btn sm" onClick={() => {
                  const data = { ...EMPTY, ...c };
                  Object.keys(data).forEach((k) => { if (data[k] === null) data[k] = EMPTY[k] ?? ""; });
                  setModal({ data, editing: true });
                }}>Modifica</button>{" "}
                <QuickActions target={{ kind: "company", id: c.id, name: c.name, email: c.email, phone: c.phone, company: c.name }} />{" "}
                <button className="btn sm danger" onClick={() => remove(c.id)}>Elimina</button>
              </td>
            </tr>
          ))}
          {list.length === 0 && <tr><td colSpan={8}><div className="empty">Nessuna azienda. Creane una o importa da file.</div></td></tr>}
        </tbody>
      </table>

      {modal && (
        <Modal title={modal.editing ? "Modifica azienda" : "Nuova azienda"} onClose={() => setModal(null)}>
          <div className="field"><label>Ragione sociale *</label><input value={modal.data.name} onChange={(e) => f("name", e.target.value)} /></div>
          <div className="grid-2">
            <div className="field"><label>Tipo</label>
              <select value={modal.data.kind} onChange={(e) => f("kind", e.target.value)}>
                {KINDS.map((k) => <option key={k.value} value={k.value}>{k.label}</option>)}
              </select>
            </div>
            <div className="field"><label>Partita IVA</label><input value={modal.data.vat_number} onChange={(e) => f("vat_number", e.target.value)} /></div>
          </div>
          <div className="grid-2">
            <div className="field"><label>Codice fiscale</label><input value={modal.data.fiscal_code} onChange={(e) => f("fiscal_code", e.target.value)} /></div>
            <div className="field"><label>Codice SDI</label><input value={modal.data.sdi_code} onChange={(e) => f("sdi_code", e.target.value)} /></div>
          </div>
          <div className="grid-2">
            <div className="field"><label>Email</label><input value={modal.data.email} onChange={(e) => f("email", e.target.value)} /></div>
            <div className="field"><label>PEC</label><input value={modal.data.pec} onChange={(e) => f("pec", e.target.value)} /></div>
          </div>
          <div className="grid-2">
            <div className="field"><label>Telefono</label><input value={modal.data.phone} onChange={(e) => f("phone", e.target.value)} /></div>
            <div className="field"><label>Indirizzo</label><input value={modal.data.address} onChange={(e) => f("address", e.target.value)} /></div>
          </div>
          <div className="grid-2">
            <div className="field"><label>Città</label><input value={modal.data.city} onChange={(e) => f("city", e.target.value)} /></div>
            <div className="grid-2">
              <div className="field"><label>Prov.</label><input value={modal.data.province} onChange={(e) => f("province", e.target.value)} /></div>
              <div className="field"><label>CAP</label><input value={modal.data.zip_code} onChange={(e) => f("zip_code", e.target.value)} /></div>
            </div>
          </div>
          <div className="grid-2">
            <div className="field"><label>Margine per acquisto (€)</label><input type="number" step="0.01" value={modal.data.margin_per_purchase} onChange={(e) => f("margin_per_purchase", e.target.value)} /></div>
            <div className="field"><label>Frequenza di acquisto (al mese)</label><input type="number" step="0.1" value={modal.data.purchase_frequency} onChange={(e) => f("purchase_frequency", e.target.value)} /></div>
          </div>
          <div className="grid-2">
            <div className="field"><label>Anno di acquisizione</label><input type="number" placeholder="es. 2022" value={modal.data.acquisition_year} onChange={(e) => f("acquisition_year", e.target.value)} /></div>
            <div className="field"><label>Portato da (referral)</label>
              <select value={modal.data.referred_by || ""} onChange={(e) => f("referred_by", e.target.value)}>
                <option value="">— Nessuno —</option>
                {allCompanies.filter((a) => a.id !== modal.data.id).map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
              </select>
            </div>
          </div>
          <div className="field"><label>Note</label><textarea rows={2} value={modal.data.notes} onChange={(e) => f("notes", e.target.value)} /></div>
          <div className="field"><label>Etichette</label><TagPicker tags={tags} selected={modal.data.tag_ids} onChange={(v) => f("tag_ids", v)} /></div>
          <div className="modal-actions">
            <button className="btn" onClick={() => setModal(null)}>Annulla</button>
            <button className="btn primary" onClick={save} disabled={!modal.data.name}>Salva</button>
          </div>
        </Modal>
      )}
    </div>
  );
}
